if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.addEventListener('change', this.onVariantChange);
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this;
      });

      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper.querySelector('.product-images');
      this.hideVariants = this.dataset.hideVariants === 'true';
      this.preOrderVariantIds = (this.dataset.preOrderVariants || '').split(',').filter(Boolean);
      this.availableOptionsCache = new Map();
      this.pendingSetDisabledFrame = null;
      this.pendingSetDisabledTimeout = null;
      this.pendingOtherUpdateFrame = null;
      this.pendingOtherUpdateTimeout = null;
      this.fieldsets = null;
      this.fieldsetControls = null;
    }

    connectedCallback() {
      this.clearInitialRequiredOptions();
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantText();

      if (this.currentVariant && this.preservedInitialRequiredOptionSelection) {
        this.updateVariantInput();
      }

      this.setDisabled();
      this.setImageSet();
    }

    clearInitialRequiredOptions() {
      if (this.requiredOptionsInitialized) {
        return;
      }

      this.requiredOptionsInitialized = true;
      this.preservedInitialRequiredOptionSelection = false;
      this.getFieldsetControls().forEach(({ fieldset, select, inputs }) => {
        if (fieldset.dataset.requiresSelection !== 'true') {
          return;
        }

        if (select) {
          const selectedOption = select.options[select.selectedIndex];

          if (selectedOption && !selectedOption.defaultSelected) {
            this.preservedInitialRequiredOptionSelection = true;
            return;
          }

          Array.from(select.options).forEach((option) => {
            option.defaultSelected = option.value === '';
          });
          select.value = '';
          return;
        }

        const checkedInput = inputs.find((input) => input.checked);

        if (checkedInput && !checkedInput.defaultChecked) {
          this.preservedInitialRequiredOptionSelection = true;
          return;
        }

        inputs.forEach((input) => {
          input.checked = false;
          input.defaultChecked = false;
        });
      });
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      const hasMissingRequiredOptions = this.hasMissingRequiredOptions();
      const isVariantUnavailable = (!this.currentVariant && !hasMissingRequiredOptions) || Boolean(this.currentVariant && !this.currentVariant.available);

      if (!hasMissingRequiredOptions) {
        this.toggleAddButton(true, '', false);
      }
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.scheduleSetDisabled();

      if (!this.currentVariant) {
        if (hasMissingRequiredOptions) {
          this.updateMedia(this.getPartialVariant());
          this.toggleAddButton(false);
        } else {
          this.toggleAddButton(true, '', true);
          this.setUnavailable();
        }
      } else {
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        this.renderProductInfo();
        //this.updateShareUrl();
      }

      this.scheduleOtherUpdate();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section,
        variantUnavailable: isVariantUnavailable
      });
    }

    updateOptions() {
      this.options = [];
      this.getFieldsetControls().forEach(({ select, inputs }) => {
        if (select) {
          this.options.push(select.value);
        } else if (inputs.length) {
          const checkedInput = inputs.find((input) => input.checked);

          this.options.push(checkedInput ? checkedInput.value : '');
        }
      });
    }
    updateVariantText() {
      this.getFieldsetControls().forEach(({ fieldset }, i) => {
        let label = fieldset.querySelector('.form__label__value');
        if (label) {
          label.innerHTML = this.options[i];
        }
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    hasMissingRequiredOptions() {
      return this.getFieldsetControls().some(({ fieldset, select, inputs }) => {
        if (fieldset.dataset.requiresSelection !== 'true') {
          return false;
        }

        if (select) {
          return select.value === '';
        }

        return !inputs.some((input) => input.checked);
      });
    }

    getPartialVariant() {
      const selectedOptions = this.options
        .map((value, index) => {
          return { value, index };
        })
        .filter(({ value }) => value !== '');

      if (!selectedOptions.length) {
        return null;
      }

      const matchingVariants = this.getVariantData().filter((variant) => {
        return selectedOptions.every(({ value, index }) => {
          return variant.options[index] === value;
        });
      });

      return matchingVariants.find((variant) => variant.featured_media) || matchingVariants[0] || null;
    }

    isPreOrderVariant(variant) {
      if (!variant) {
        return false;
      }

      if (this.preOrderVariantIds.includes(String(variant.id))) {
        return true;
      }

      if (!variant.inventory_management || variant.inventory_policy !== 'continue') {
        return false;
      }

      const inventoryQuantity = Number(variant.inventory_quantity);

      return Number.isFinite(inventoryQuantity) && inventoryQuantity <= 0;
    }

    getFieldsetControls() {
      this.fieldsets = this.fieldsets || Array.from(this.querySelectorAll('fieldset'));

      if (!this.fieldsetControls || this.fieldsetControls.length !== this.fieldsets.length) {
        this.fieldsetControls = this.fieldsets.map((fieldset) => {
          const select = fieldset.querySelector('select');

          return {
            fieldset,
            select,
            options: select ? Array.from(select.options) : [],
            inputs: select ? [] : Array.from(fieldset.querySelectorAll('input'))
          };
        });
      }

      return this.fieldsetControls;
    }

    scheduleSetDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }

      if (this.pendingSetDisabledFrame) {
        window.cancelAnimationFrame(this.pendingSetDisabledFrame);
      }
      if (this.pendingSetDisabledTimeout) {
        window.clearTimeout(this.pendingSetDisabledTimeout);
      }

      this.pendingSetDisabledFrame = window.requestAnimationFrame(() => {
        this.pendingSetDisabledFrame = null;
        this.pendingSetDisabledTimeout = window.setTimeout(() => {
          this.pendingSetDisabledTimeout = null;
          this.setDisabled();
        }, 0);
      });
    }

    scheduleOtherUpdate() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }

      if (this.pendingOtherUpdateFrame) {
        window.cancelAnimationFrame(this.pendingOtherUpdateFrame);
      }
      if (this.pendingOtherUpdateTimeout) {
        window.clearTimeout(this.pendingOtherUpdateTimeout);
      }

      this.pendingOtherUpdateFrame = window.requestAnimationFrame(() => {
        this.pendingOtherUpdateFrame = null;
        this.pendingOtherUpdateTimeout = window.setTimeout(() => {
          this.pendingOtherUpdateTimeout = null;
          this.updateOther();
        }, 0);
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        let fieldsets_array = this.other[0].getFieldsetControls();
        this.options.forEach((option, i) => {
          const fieldsetControl = fieldsets_array[i];

          if (!fieldsetControl) {
            return;
          }

          if (fieldsetControl.select) {
            fieldsetControl.select.value = option;
          } else if (fieldsetControl.inputs.length) {
            const input = fieldsetControl.inputs.find((item) => item.value === option);

            if (input) {
              input.checked = true;
            }
          }
        });
        this.other[0].updateOptions();
        this.other[0].updateMasterId();
        this.other[0].updateVariantText();
        this.other[0].scheduleSetDisabled();
      }
    }

    updateMedia(variant = this.currentVariant) {
      if (!variant) return;
      if (!variant.featured_media) return;

      let thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');

      this.setActiveMedia(`#Slide-${this.dataset.section}-${variant.featured_media.id}`, `#Thumb-${this.dataset.section}-${variant.featured_media.id}`, this.productSlider, thumbnails, variant);
    }
    setActiveMedia(mediaId, thumbId, productSlider, thumbnails, variant = this.currentVariant) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);

      if (!activeMedia) return;

      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (thumbnails) {
          if (thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia(variant);

        if (thumbnails) {
          let activeThumb = thumbnails.querySelector(thumbId);

          if (activeThumb) {
            activeThumb.classList.add('is-active');
            activeThumb.classList.add('is-initial-selected');
          }
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      section.querySelectorAll('product-form').forEach((productForm) => {
        productForm.handleErrorMessage();
        productForm.clearRequiredOptionErrors?.();
      });
    }

    getSectionsToRender() {
      return [`badges-${this.dataset.section}`, `price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    renderProductInfo() {
      let sections = this.getSectionsToRender();

      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          sections.forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(id);
            const price_fixed = document.getElementById(id + '--sticky');

            if (price) price.classList.remove('visibility-hidden');
            if (price_fixed) price_fixed.classList.remove('visibility-hidden');

          });
          this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);

        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      const preOrderNote = productForm.querySelector('[data-pre-order-note]');
      const isPreOrder = !disable && (productTemplate?.includes('pre-order') || this.isPreOrderVariant(this.currentVariant));
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      if (preOrderNote) preOrderNote.hidden = !isPreOrder;

      if (!submitButtons) return;
      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');

          if (isPreOrder) {
            submitButtonText.textContent = window.theme.variantStrings.preOrder;
          } else {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }

        }
      });

      if (!modifyClass) return;
    }

    setUnavailable() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const badges = document.getElementById(`badges-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButton) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (badges) badges.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();


      if (variant_data) {

        let selected_options = false;
        if (this.currentVariant) {
          selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });
        } else {
          let found_option = variant_data.find(option => {
            return option.option1 === this.options[0];
          });
          if (found_option) {
            selected_options = [
              {
                "value": this.options[0],
                "index": "option1"
              },
              {
                "value": found_option.option2,
                "index": "option2"
              }
            ];
          } else {
            return;
          }
        }
        const available_options = this.getAvailableOptions(variant_data, selected_options);

        this.getFieldsetControls().forEach(({ select, options, inputs }, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (select) {
              fieldset_options.forEach((option, option_i) => {
                const optionElement = options.find((item) => item.value === option.value);

                if (optionElement) {
                  optionElement.disabled = option.isUnavailable;
                }
              });
            } else if (inputs.length) {
              inputs.forEach((input, input_i) => {
                const fieldsetOption = fieldset_options[input_i];

                if (fieldsetOption) {
                  input.classList.toggle('is-disabled', fieldsetOption.isUnavailable);
                }
              });
            }
          }
        });

      }
      return true;
    }

    getAvailableOptions(variants, selectedOptions) {
      const cacheKey = selectedOptions.map(({ value, index }) => `${index}:${value}`).join('|');

      if (this.availableOptionsCache.has(cacheKey)) {
        return this.availableOptionsCache.get(cacheKey);
      }

      const availableOptions = this.createAvailableOptionsTree(variants, selectedOptions);
      this.availableOptionsCache.set(cacheKey, availableOptions);

      return availableOptions;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;
        this.setImageSetMedia(this.currentVariant || this.getPartialVariant());
      }
    }
    setImageSetMedia(variant = this.currentVariant) {
      if (!this.imageSetIndex || !variant) {
        return;
      }

      let setValue = this.getImageSetName(variant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

    createAvailableOptionsTree(variants, currentlySelectedValues) {
      // Reduce variant array into option availability tree
      const currentOption1 = currentlySelectedValues.find(({ index }) => index === 'option1');
      const currentOption2 = currentlySelectedValues.find(({ index }) => index === 'option2');
      const optionIndexes = ['option1', 'option2', 'option3'];

      return variants.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        optionIndexes.forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = { value: variant[index], isUnavailable: true };
            options[index].push(entry);
          }

          switch (index) {
            case 'option1':
              // Option1 inputs should always remain enabled based on all available variants
              entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              break;
            case 'option2':
              // Option2 inputs should remain enabled based on available variants that match first option group
              if (currentOption1 && variant.option1 === currentOption1.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
              break;
            case 'option3':
              // Option 3 inputs should remain enabled based on available variants that match first and second option group
              if (currentOption1 && variant.option1 === currentOption1.value && currentOption2 && variant.option2 === currentOption2.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
          }
        });

        return options;
      }, { option1: [], option2: [], option3: [] });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      this.options = this.getFieldsetControls().map(({ inputs }) => {
        const checkedInput = inputs.find((radio) => radio.checked);

        return checkedInput ? checkedInput.value : '';
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupProductGallery);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.setOptions();
      // Start Slider
      this.init();
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = {
        wrapAround: true,
        pageDots: true,
        contain: true,
        adaptiveHeight: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };
    }
    init() {
      this.flkty = new Flickity(this, this.options);

      this.selectedIndex = this.flkty.selectedIndex;

      // Setup Events
      this.setupEvents();

      // Start Gallery
      this.setupProductGallery();
    }
    reInit(imageSetIndex) {

      this.flkty.destroy();

      this.setOptions();

      this.flkty = new Flickity(this, this.options);

      // Setup Events
      this.setupEvents();

      this.selectedIndex = this.flkty.selectedIndex;

      this.scheduleResize();
    }
    resize(mediaId) {
      if (!this.flkty || !this.isConnected) return;

      this.flkty.resize();
      this.flkty.reposition();

      if (mediaId) {
        this.flkty.selectCell(mediaId);
        this.selectedIndex = this.flkty.selectedIndex;
      }
    }
    scheduleResize(mediaId) {
      requestAnimationFrame(() => {
        this.resize(mediaId);

        setTimeout(() => {
          this.resize(mediaId);
        }, 260);
      });
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectAdjacentCell(-1);
        });
        prev.addEventListener('keyup', (event) => {
          event.preventDefault();
          this.selectAdjacentCell(-1);
        });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectAdjacentCell(1);
        });
        next.addEventListener('keyup', (event) => {
          event.preventDefault();
          this.selectAdjacentCell(1);
        });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
      this.flkty.on('settle', function (index) {
        _this.selectedIndex = index;
      });
      this.flkty.on('change', (index) => {

        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
          active_thumb = this.getThumbnailForSlide(this.flkty.selectedElement) || (active_thumbs[index] ? active_thumbs[index] : active_thumbs[0]);

        this.setActiveThumbnail(active_thumb);

        // Stop previous video
        if (previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: ""
            }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
        } else if (previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video').pause();
        }

      });


      setTimeout(() => {
        let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
        active_thumbs.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            this.thumbnailClick(thumbnail);
          });
        });
      });

    }
    thumbnailClick(thumbnail) {
      [].forEach.call(this.thumbnails, function (el) {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      const mediaId = '#' + thumbnail.id.replace('Thumb', 'Slide');
      this.selectCell(mediaId);
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      this.flkty.selectCell(mediaId);
      this.selectedIndex = this.flkty.selectedIndex;
      this.syncThumbnail(mediaId);
      this.scheduleResize(mediaId);
    }
    selectAdjacentCell(direction) {
      const activeThumbs = Array.from(this.thumbnails).filter((thumbnail) => thumbnail.classList.contains('is-active'));

      if (!activeThumbs.length) return;

      const currentThumb = activeThumbs.find((thumbnail) => thumbnail.classList.contains('is-initial-selected')) || this.getThumbnailForSlide(this.flkty.selectedElement) || activeThumbs[0];
      const currentIndex = activeThumbs.indexOf(currentThumb);
      const nextIndex = currentIndex >= 0 ? (currentIndex + direction + activeThumbs.length) % activeThumbs.length : 0;
      const nextThumb = activeThumbs[nextIndex];

      if (!nextThumb || !nextThumb.id) return;

      this.selectCell('#' + nextThumb.id.replace('Thumb', 'Slide'));
    }
    getThumbnailForSlide(slide) {
      if (!slide || !slide.id) return null;

      const thumbId = slide.id.replace('Slide-', 'Thumb-');

      return Array.from(this.thumbnails).find((thumbnail) => thumbnail.id === thumbId);
    }
    setActiveThumbnail(activeThumb) {
      if (!activeThumb) return;

      this.thumbnails.forEach((item) => {
        item.classList.remove('is-initial-selected');
      });
      activeThumb.classList.add('is-initial-selected');

      requestAnimationFrame(() => {
        if (activeThumb.offsetParent === null) {
          return;
        }
        const windowHalfHeight = activeThumb.offsetParent.clientHeight / 2,
          windowHalfWidth = activeThumb.offsetParent.clientWidth / 2;
        activeThumb.parentElement.scrollTo({
          left: activeThumb.offsetLeft - windowHalfWidth + activeThumb.clientWidth / 2,
          top: activeThumb.offsetTop - windowHalfHeight + activeThumb.clientHeight / 2,
          behavior: 'smooth'
        });
      });
    }
    syncThumbnail(mediaId) {
      if (!mediaId || !this.thumbnails) return;

      const slideId = mediaId.replace(/^#/, '');
      const thumbId = slideId.replace('Slide-', 'Thumb-');
      const activeThumb = Array.from(this.thumbnails).find((thumbnail) => thumbnail.id === thumbId);

      this.setActiveThumbnail(activeThumb);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);

        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];

      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };


      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        document.body.classList.add('open-lightbox');
        pswp.listen('firstUpdate', function () {
          pswp.listen('destroy', function () {
            document.body.classList.remove('open-lightbox');
          });
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 50
            };
          });
        });
        pswp.init();
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.onRequiredOptionOverlayClick = (event) => {
        const overlay = event.target.closest('[data-required-option-overlay]');

        if (!overlay) {
          return;
        }

        event.preventDefault();
        this.validateRequiredOptions(overlay);
      };
      this.onRequiredOptionStateChange = (event) => {
        if (event.detail?.sectionId === this.dataset.section) {
          this.updateRequiredOptionState();
          this.updateVariantPurchaseState(event.detail?.variantUnavailable);
        }
      };
      this.addEventListener('click', this.onRequiredOptionOverlayClick);
      document.addEventListener('product:variant-change', this.onRequiredOptionStateChange);
      this.updateRequiredOptionState();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onRequiredOptionOverlayClick);
      document.removeEventListener('product:variant-change', this.onRequiredOptionStateChange);
    }
    onSubmitHandler(evt) {
      evt.preventDefault();

      if (!this.form.reportValidity()) {
        return;
      }

      if (!this.validateRequiredOptions(evt.submitter)) {
        return;
      }

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleSectionErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };


      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    getSectionProductForms() {
      return Array.from(document.querySelectorAll('product-form')).filter((productForm) => {
        return productForm.dataset.section === this.dataset.section;
      });
    }
    handleSectionErrorMessage(errorMessage = false) {
      this.getSectionProductForms().forEach((productForm) => {
        productForm.handleErrorMessage?.(errorMessage);
      });
    }
    getRequiredOptionGroups() {
      const groups = new Map();
      const variantSelectors = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector.dataset.section === this.dataset.section;
      });

      variantSelectors.forEach((selector) => {
        selector.querySelectorAll('[data-requires-selection="true"]').forEach((fieldset) => {
          const key = fieldset.dataset.index || fieldset.dataset.handle;

          if (!groups.has(key)) {
            groups.set(key, []);
          }

          groups.get(key).push(fieldset);
        });
      });

      return Array.from(groups.values());
    }
    hasRequiredOptionValue(fieldset) {
      const select = fieldset.querySelector('select');

      if (select) {
        return select.value !== '';
      }

      return Array.from(fieldset.querySelectorAll('input')).some((input) => input.checked);
    }
    clearRequiredOptionErrors() {
      this.getRequiredOptionGroups().flat().forEach((fieldset) => {
        fieldset.classList.remove('is-missing-required');
        fieldset.removeAttribute('aria-invalid');
      });
    }
    getMissingRequiredOptionGroup() {
      return this.getRequiredOptionGroups().find((fieldsets) => {
        return !fieldsets.some((fieldset) => this.hasRequiredOptionValue(fieldset));
      });
    }
    updateRequiredOptionState() {
      const hasMissingRequiredOption = Boolean(this.getMissingRequiredOptionGroup());

      this.getSectionProductForms().forEach((productForm) => {
        productForm.classList.toggle('product-form--missing-required-option', hasMissingRequiredOption);
      });

      return hasMissingRequiredOption;
    }
    updateVariantPurchaseState(isVariantUnavailable = false) {
      this.getSectionProductForms().forEach((productForm) => {
        productForm.classList.toggle('product-form--variant-unavailable', Boolean(isVariantUnavailable));
      });
    }
    validateRequiredOptions(submitter = null) {
      const missingOptionGroup = this.getMissingRequiredOptionGroup();

      this.clearRequiredOptionErrors();
      this.updateRequiredOptionState();

      if (!missingOptionGroup) {
        return true;
      }

      const message = this.dataset.requiredOptionMessage || window.theme?.variantStrings?.selectSize || 'Please select a size.';
      const submitterSticky = submitter?.closest('product-add-to-cart-sticky');
      const preferredFieldset = submitterSticky ? missingOptionGroup.find((fieldset) => submitterSticky.contains(fieldset)) : null;
      const visibleFieldset = preferredFieldset || missingOptionGroup.find((fieldset) => fieldset.offsetParent !== null) || missingOptionGroup[0];

      missingOptionGroup.forEach((fieldset) => {
        fieldset.classList.add('is-missing-required');
        fieldset.setAttribute('aria-invalid', 'true');
      });

      this.handleSectionErrorMessage(message);

      if (visibleFieldset) {
        const focusTarget = visibleFieldset.querySelector('select, input:not(.is-disabled)');

        visibleFieldset.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        if (focusTarget) {
          focusTarget.focus({
            preventScroll: true
          });
        }
      }

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }
        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));

      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer');

      if (search_drawer.classList.contains('active')) {
        search_drawer.classList.remove('active');
      }
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');
        this.body.classList.remove('open-cc--product');
        if (document.getElementById('Cart-Drawer')) {
          const cartDrawer = document.getElementById('Cart-Drawer');
          if (typeof cartDrawer.openDrawer === 'function') {
            cartDrawer.openDrawer();
          } else if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      } else if (document.getElementById('Cart-Drawer')) {
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (typeof cartDrawer.openDrawer === 'function') {
          cartDrawer.openDrawer();
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}


/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();

      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }
    connectedCallback() {
      this.setupObservers();
      this.setupToggle();
      this.setupMobileButtonText();
    }
    disconnectedCallback() {
      if (this.mobileButtonTextObserver) {
        this.mobileButtonTextObserver.disconnect();
      }
    }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner'),
        content = this.querySelector('.product-add-to-cart-sticky--content');

      if (!button || !content) {
        return;
      }

      const setExpandedState = (expanded) => {
        this.classList.toggle('sticky-expanded', expanded);
        button.classList.toggle('sticky-open', expanded);
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      if (this.animations_enabled) {
        const tl = gsap.timeline({
          reversed: true,
          paused: true,
          onStart: () => {
            setExpandedState(true);
          },
          onReverseComplete: () => {
            setExpandedState(false);
          }
        });

        tl
          .set(content, {
            display: 'block',
            height: 'auto'
          }, 'start')
          .from(content, {
            height: 0,
            duration: 0.25
          }, 'start+=0.001');

        button.addEventListener('click', function (event) {
          event.preventDefault();
          tl.reversed() ? tl.play() : tl.reverse();
        });
      } else {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          const expanded = !content.classList.contains('active');
          content.classList.toggle('active', expanded);
          setExpandedState(expanded);
        });
      }

    }
    setupMobileButtonText() {
      const mobileButtonText = this.querySelector('.product-add-to-cart-sticky--mobile-cta'),
        submitButtonText = this.querySelector('#AddToCartSticky .single-add-to-cart-button--text');

      if (!mobileButtonText || !submitButtonText || typeof MutationObserver === 'undefined') {
        return;
      }

      const syncText = () => {
        mobileButtonText.textContent = submitButtonText.textContent.trim();
      };

      syncText();
      this.mobileButtonTextObserver = new MutationObserver(syncText);
      this.mobileButtonTextObserver.observe(submitButtonText, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      observer.observe(form);
      observer.observe(footer);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

/**
 *  @class
 *  @function ProductSidePanelLinks
 */
if (!customElements.get('side-panel-links')) {
  class ProductSidePanelLinks extends HTMLElement {
    constructor() {
      super();
      this.links = this.querySelectorAll('button');
      this.drawer = document.getElementById('Product-Information-Drawer');
      this.buttons = this.drawer.querySelector('.side-panel-content--tabs');
      this.panels = this.drawer.querySelector('.side-panel-content--inner').querySelectorAll('.side-panel-content--tab-panel');
      this.body = document.body;
    }
    connectedCallback() {
      this.setupObservers();
    }
    disconnectedCallback() {

    }
    setupObservers() {
      this.links.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.body.classList.add('open-cc');
          this.buttons.toggleActiveClass(i);
          this.drawer.classList.add('active');
        });
      });
    }
  }

  customElements.define('side-panel-links', ProductSidePanelLinks);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}
